/**
 * THEMES.
 *
 * TWO, and only two: Dark and Light. Both are the Jovii brand design language
 * (softened chamfer, crimson) — the same hue, the same mark, once on the brand's
 * black and once on the wine that is Light. The five studio palettes that used to live
 * here (Rouge, Deep sea, Mono, Jovi, Wildcard) are gone: the brand is settled
 * now, and a picker offering acid green on aubergine is offering to leave it.
 *
 * Each theme only overrides colour tokens — never a radius, a duration or a
 * layout number — so a theme cannot change the shape of the app, only its
 * light. `app.css` remains the default and the definition of every name.
 *
 * WHY DARK CARRIES OVERRIDES AND LIGHT CARRIES MORE. Dark is the default, so
 * it could have been expressed as "no overrides" the way Rouge was. It is not:
 * app.css still holds the pre-brand neutral greys, and the brand ground is
 * wine. Dark restates the palette; app.css keeps owning every NAME. The moment
 * app.css is repainted in brand colours, Dark's token list can shrink to {}.
 *
 * LIGHT IS NOT DARK WITH THE LUMINANCE FLIPPED. Three things genuinely invert
 * rather than scale, and each one is a bug if it is merely darkened:
 *   - `color-scheme`, so form controls, scrollbars and the caret follow.
 *   - The glass edge. A hairline of WHITE along a top edge reads as a lit
 *     bevel on a dark ground and as nothing at all on paper; on light it
 *     becomes a hairline of the ink colour, and the shine becomes a shadow.
 *   - `--focus-ring`. Selection reads by LUMINANCE, never by the accent — on
 *     dark that means white, on light it means near-black. Same rule, opposite
 *     end of the ramp.
 * Those live in the `html[data-theme='light']` block in app.css, because they
 * are not colour tokens and `applyTheme()` only writes colour tokens.
 *
 * The choice is per-device and lives in `localStorage`, like every other
 * preference in this app. It is applied to `<html>` as `data-theme`, written
 * BEFORE the app mounts (see `main.ts`) so the first paint is already the right
 * colour and nobody sees the wrong ground flash past.
 *
 * Every pair below is measured by `pnpm run check:contrast`, which reads BOTH
 * themes out of this file as well as the tokens in app.css. A theme that drops
 * text under its floor fails the build.
 */
export type ThemeId = 'dark' | 'light';

export interface Theme {
  id: ThemeId;
  name: string;
  note: string;
  /** Token overrides. Colour only — anything else belongs in app.css. */
  tokens: Record<string, string>;
}

const KEY = 'jt.theme.v1';

/**
 * `--person-1..12` are the identity hues behind every avatar mark (see the
 * People block in app.css). Both themes re-key all twelve so a mark recolours
 * live with the theme — same slot, same person, the theme's light. On Light
 * they are darkened rather than reused: the dark set is tuned to glow on a
 * near-black tile and washes out to pastel on paper.
 */
export const THEMES: Theme[] = [
  {
    id: 'dark',
    name: 'Dark',
    note: 'Crimson on black',
    tokens: {
      /* BLACK, with the faintest wine in it so it is the brand's black and not
         a neutral grey. The owner's brief: "dark mode should be a black
         background with a little bit of texture and a little bit of motion"
         - the texture and motion are LivingGround.svelte; this is the ground
         it draws on. Surfaces climb toward the wine, so a stack of cards reads
         as one material lit from above. */
      '--bg': '#0a0607',
      '--surface-1': '#141011',
      '--surface-2': '#1b1516',
      '--surface-3': '#241c1e',
      '--line': '#2f2528',
      '--text': '#f7f1e8',
      '--text-dim': '#c7b8b6',
      '--text-faint': '#9d8d8c',
      '--accent': '#ef2034',
      '--accent-hot': '#ff4a5a',
      '--accent-ink': '#1a0508',
      '--accent-warm': '#f26a4f',
      '--accent-deep': '#b8101f',
      '--focus-ring': '#ffd0d6',
      '--live': '#39c9a3',
      '--good': '#4fcf8a',
      '--warn': '#f2b447',
      '--bad': '#ff5c5c',
      '--person-1': '#e0575b',
      '--person-2': '#e08a3c',
      '--person-3': '#d7b13a',
      '--person-4': '#7fb03c',
      '--person-5': '#3fae7a',
      '--person-6': '#39a9b8',
      '--person-7': '#4b90e2',
      '--person-8': '#6f6fe0',
      '--person-9': '#a05fd6',
      '--person-10': '#d155a6',
      '--person-11': '#c47a5a',
      '--person-12': '#8a9aa8'
    }
  },
  {
    id: 'light',
    name: 'Light',
    note: 'Ivory on wine',
    tokens: {
      /* NOT A PAPER THEME. Two cuts of this were rejected - ivory with crimson
         accents ("just white"), then a pink blush ("way too light"). The brief,
         verbatim: "like a dark mode, but with the themes and the palette of the
         brand." So Light is Dark's lighter sibling: the ground is the brand
         WINE lifted a step toward crimson, the surfaces climb further up that
         same ramp, and the text stays ivory. Next to Dark's black this reads
         as the lit, coloured version of the same room - which is the point. */
      '--bg': '#520d19',
      '--surface-1': '#600f1b',
      '--surface-2': '#6e101e',
      '--surface-3': '#7c1220',
      '--line': '#8d1423',
      '--text': '#f7f1e8',
      '--text-dim': '#f0cfd0',
      '--text-faint': '#e2b5b8',
      /* On a wine ground the crimson accent has no contrast, so the accent is
         its light tint: still the brand hue, readable at 6.6:1. Hot is lighter
         still. Ink on the action is the wine-black. */
      '--accent': '#ff9aa6',
      '--accent-hot': '#ffc3ca',
      '--accent-ink': '#2a0509',
      '--accent-warm': '#ffb08a',
      '--accent-deep': '#ff6b7a',
      '--focus-ring': '#fff1f2',
      '--live': '#7ce5c8',
      '--good': '#8be3ac',
      '--warn': '#ffd27a',
      '--bad': '#ff9c9c',
      '--person-1': '#e0575b',
      '--person-2': '#e08a3c',
      '--person-3': '#d7b13a',
      '--person-4': '#7fb03c',
      '--person-5': '#3fae7a',
      '--person-6': '#39a9b8',
      '--person-7': '#4b90e2',
      '--person-8': '#6f6fe0',
      '--person-9': '#a05fd6',
      '--person-10': '#d155a6',
      '--person-11': '#c47a5a',
      '--person-12': '#8a9aa8'
    }
  }
];

export function themeById(id: string | null): Theme {
  return THEMES.find((t) => t.id === id) ?? THEMES[0]!;
}

/**
 * The stored choice. Safe to call before the DOM exists.
 *
 * With no stored choice we follow the SYSTEM, which is what a two-theme app is
 * expected to do and what the five-palette picker could not meaningfully offer.
 * An explicit choice always wins and is never overwritten by the OS.
 */
export function storedTheme(): ThemeId {
  if (typeof localStorage === 'undefined') return 'dark';
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === 'dark' || saved === 'light') return saved;
  } catch {
    /* private mode; fall through to the system preference */
  }
  return systemTheme();
}

/** What the OS asks for, defaulting to dark when it has no opinion. */
export function systemTheme(): ThemeId {
  if (typeof matchMedia === 'undefined') return 'dark';
  try {
    return matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

/** Has the viewer made a choice, or are we still following the OS? */
export function hasStoredTheme(): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    const saved = localStorage.getItem(KEY);
    return saved === 'dark' || saved === 'light';
  } catch {
    return false;
  }
}

/**
 * Paint it. Every theme's tokens are REMOVED first and then the chosen set is
 * written, so switching from a theme that overrides a token to one that does
 * not cannot leave the old value behind — the classic bug with patch-style
 * theming.
 *
 * `data-theme` on <html> is what the non-colour half of the theme hangs off
 * (see the light block in app.css): `color-scheme`, the glass edge and the
 * shadow ramp are not colour tokens and cannot be written from here.
 */
export function applyTheme(id: ThemeId): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  for (const theme of THEMES) {
    for (const name of Object.keys(theme.tokens)) root.style.removeProperty(name);
  }
  const chosen = themeById(id);
  for (const [name, value] of Object.entries(chosen.tokens)) {
    root.style.setProperty(name, value);
  }
  root.dataset.theme = chosen.id;
}

export function setTheme(id: ThemeId): void {
  applyTheme(id);
  try {
    localStorage.setItem(KEY, id);
  } catch {
    /* private mode; the choice lasts this session and that is fine */
  }
}

/** The live choice, as a rune, for the picker to bind against. */
export const theme = $state<{ id: ThemeId }>({ id: 'dark' });

export function initTheme(): void {
  theme.id = storedTheme();
  applyTheme(theme.id);
  followSystem();
}

/**
 * While the viewer has made no explicit choice, track the OS. Once they pick a
 * side we stop listening — an app that keeps overriding a deliberate choice at
 * sunset is broken, not clever.
 */
function followSystem(): void {
  if (typeof matchMedia === 'undefined') return;
  try {
    matchMedia('(prefers-color-scheme: light)').addEventListener('change', () => {
      if (hasStoredTheme()) return;
      theme.id = systemTheme();
      applyTheme(theme.id);
    });
  } catch {
    /* Safari < 14 has no addEventListener here; the initial read still worked */
  }
}

export function chooseTheme(id: ThemeId): void {
  theme.id = id;
  setTheme(id);
}